<script setup>
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { picoSearch } from '@scmmishra/pico-search';
import { useStore } from 'dashboard/composables/store';
import { useMapGetter } from 'dashboard/composables/store';
import { useUISettings } from 'dashboard/composables/useUISettings';

import PageLayout from 'dashboard/components-next/polaris/PageLayout.vue';
import SuggestedRules from 'dashboard/components-next/polaris/cards/SuggestedRules.vue';
import AddRuleField from 'dashboard/components-next/polaris/cards/AddRuleField.vue';
import ListToolbar from 'dashboard/components-next/polaris/cards/ListToolbar.vue';
import RuleCardSkin from 'dashboard/components-next/polaris/skin/RuleCardSkin.vue';
import BulkSelectBar from 'dashboard/components-next/polaris/assistant/BulkSelectBar.vue';
import PolarisEmptyState from 'dashboard/components-next/polaris/pageComponents/emptyStates/PolarisEmptyState.vue';
import {
  GUIDANCE_CATEGORIES,
  guidanceToCard,
  groupGuidanceCards,
  guidanceWithEdit,
} from 'dashboard/components-next/polaris/pageComponents/guidance/guidanceCategories';

// Guardrails (phase 4.4): the kit's plant, shared with Guidelines — the explanation, the kit's SuggestedRules, the
// kit's RuleCard (through RuleCardSkin: edit in place, Enter saves, Escape leaves), the ListToolbar and the kit's
// AddRuleField. The way back to Settings is the Topbar's breadcrumb (4.1). Same store calls and messages.
const { t, tm, rt } = useI18n();
const route = useRoute();
const store = useStore();
const { uiSettings, updateUISettings } = useUISettings();

const uiFlags = useMapGetter('polarisAssistants/getUIFlags');
const assistantId = computed(() => Number(route.params.assistantId));
const isFetching = computed(() => uiFlags.value.fetchingItem);
const assistant = computed(() =>
  store.getters['polarisAssistants/getRecord'](assistantId.value)
);

const searchQuery = ref('');

const guardrailsContent = computed(() => assistant.value?.guardrails || []);

const displayGuardrails = computed(() =>
  guardrailsContent.value.map((c, idx) => guidanceToCard(c, idx))
);

// Fase 3: agrupado por categoria (strings legadas caem em communication_style sem título).
const categoryOptions = computed(() =>
  GUIDANCE_CATEGORIES.map(value => ({
    value,
    label: t(`POLARIS.GUIDANCE.CATEGORIES.${value.toUpperCase()}`),
  }))
);

// Suggested guardrails (POLARIS.PAGES.GUARDRAIL_EXAMPLES, en and pt_BR)
const guardrailsExample = computed(() =>
  tm('POLARIS.PAGES.GUARDRAIL_EXAMPLES').map((content, index) => ({
    id: index + 1,
    content: rt(content),
  }))
);

const filteredGuardrails = computed(() => {
  const query = searchQuery.value.trim();
  if (!query) return displayGuardrails.value;
  return picoSearch(displayGuardrails.value, query, ['content']);
});

const groupedGuardrails = computed(() =>
  groupGuidanceCards(filteredGuardrails.value)
);

const shouldShowSuggestedRules = computed(() => {
  return uiSettings.value?.show_guardrails_suggestions !== false;
});

const closeSuggestedRules = () => {
  updateUISettings({ show_guardrails_suggestions: false });
};

// Bulk selection
const bulkSelectedIds = ref(new Set());

const handleRuleSelect = id => {
  const selected = new Set(bulkSelectedIds.value);
  selected[selected.has(id) ? 'delete' : 'add'](id);
  bulkSelectedIds.value = selected;
};

const buildSelectedCountLabel = computed(() => {
  const count = displayGuardrails.value.length || 0;
  const isAllSelected = bulkSelectedIds.value.size === count && count > 0;
  return isAllSelected
    ? t('POLARIS.ASSISTANTS.GUARDRAILS.BULK_ACTION.UNSELECT_ALL', { count })
    : t('POLARIS.ASSISTANTS.GUARDRAILS.BULK_ACTION.SELECT_ALL', { count });
});

const selectedCountLabel = computed(() => {
  return t('POLARIS.ASSISTANTS.GUARDRAILS.BULK_ACTION.SELECTED', {
    count: bulkSelectedIds.value.size,
  });
});

const saveGuardrails = async list => {
  await store.dispatch('polarisAssistants/update', {
    id: assistantId.value,
    assistant: { guardrails: list },
  });
};

const addGuardrail = async (content, category = '') => {
  try {
    // Categoria fora do estilo de comunicação vira item {category, text}; o padrão segue string (legado).
    const item =
      category && category !== 'communication_style'
        ? { category, text: content }
        : content;
    const newGuardrails = [...guardrailsContent.value, item];
    await saveGuardrails(newGuardrails);
    useAlert(t('POLARIS.ASSISTANTS.GUARDRAILS.API.ADD.SUCCESS'));
  } catch (error) {
    useAlert(t('POLARIS.ASSISTANTS.GUARDRAILS.API.ADD.ERROR'));
  }
};

const editGuardrail = async ({ id, content }) => {
  try {
    const updated = guidanceWithEdit(guardrailsContent.value, id, content);
    await saveGuardrails(updated);
    useAlert(t('POLARIS.ASSISTANTS.GUARDRAILS.API.UPDATE.SUCCESS'));
  } catch {
    useAlert(t('POLARIS.ASSISTANTS.GUARDRAILS.API.UPDATE.ERROR'));
  }
};

const deleteGuardrail = async id => {
  try {
    const updated = guardrailsContent.value.filter((_, idx) => idx !== id);
    await saveGuardrails(updated);
    useAlert(t('POLARIS.ASSISTANTS.GUARDRAILS.API.DELETE.SUCCESS'));
  } catch {
    useAlert(t('POLARIS.ASSISTANTS.GUARDRAILS.API.DELETE.ERROR'));
  }
};

const bulkDeleteGuardrails = async () => {
  try {
    if (bulkSelectedIds.value.size === 0) return;
    const updated = guardrailsContent.value.filter(
      (_, idx) => !bulkSelectedIds.value.has(idx)
    );
    await saveGuardrails(updated);
    bulkSelectedIds.value.clear();
    useAlert(t('POLARIS.ASSISTANTS.GUARDRAILS.API.DELETE.SUCCESS'));
  } catch {
    useAlert(t('POLARIS.ASSISTANTS.GUARDRAILS.API.DELETE.ERROR'));
  }
};

const addAllExample = () => {
  updateUISettings({ show_guardrails_suggestions: false });
  try {
    const exampleContents = guardrailsExample.value.map(
      example => example.content
    );
    const newGuardrails = [...guardrailsContent.value, ...exampleContents];
    saveGuardrails(newGuardrails);
  } catch {
    useAlert(t('POLARIS.ASSISTANTS.GUARDRAILS.API.ADD.ERROR'));
  }
};
</script>

<template>
  <PageLayout
    preview="open"
    :header-title="$t('POLARIS.ASSISTANTS.GUARDRAILS.TITLE')"
    :is-fetching="isFetching"
    :show-know-more="false"
    :show-pagination-footer="false"
  >
    <template #body>
      <p class="max-w-prose type-polaris-body text-polaris-muted-foreground">
        {{ $t('POLARIS.ASSISTANTS.GUARDRAILS.DESCRIPTION') }}
      </p>
      <SuggestedRules
        v-if="shouldShowSuggestedRules"
        :title="$t('POLARIS.ASSISTANTS.GUARDRAILS.ADD.SUGGESTED.TITLE')"
        :items="guardrailsExample"
        @add="item => addGuardrail(item.content)"
        @add-all="addAllExample"
        @close="closeSuggestedRules"
      />
      <template v-if="displayGuardrails.length">
        <ListToolbar
          v-if="bulkSelectedIds.size === 0"
          v-model:search="searchQuery"
          :placeholder="
            t('POLARIS.ASSISTANTS.GUARDRAILS.LIST.SEARCH_PLACEHOLDER')
          "
          :count="displayGuardrails.length"
          count-key="POLARIS.PAGES.RULES_COUNT"
        />
        <BulkSelectBar
          v-else
          v-model="bulkSelectedIds"
          :all-items="displayGuardrails"
          :select-all-label="buildSelectedCountLabel"
          :selected-count-label="selectedCountLabel"
          :delete-label="
            $t('POLARIS.ASSISTANTS.GUARDRAILS.BULK_ACTION.BULK_DELETE_BUTTON')
          "
          @bulk-delete="bulkDeleteGuardrails"
        />
      </template>
      <PolarisEmptyState
        v-if="displayGuardrails.length === 0"
        :title="t('POLARIS.ASSISTANTS.GUARDRAILS.EMPTY_MESSAGE')"
      />
      <PolarisEmptyState
        v-else-if="filteredGuardrails.length === 0"
        :title="t('POLARIS.ASSISTANTS.GUARDRAILS.SEARCH_EMPTY_MESSAGE')"
      />
      <div v-else class="flex flex-col gap-5">
        <section
          v-for="group in groupedGuardrails"
          :key="group.category"
          class="flex flex-col gap-2"
        >
          <h2
            class="type-polaris-caption font-semibold uppercase tracking-wide text-polaris-muted-foreground"
          >
            {{
              t(`POLARIS.GUIDANCE.CATEGORIES.${group.category.toUpperCase()}`)
            }}
          </h2>
          <div v-for="guardrail in group.items" :key="guardrail.id">
            <p
              v-if="guardrail.title"
              class="px-1 pb-0.5 type-polaris-caption text-polaris-muted-foreground"
            >
              {{ guardrail.title }}
            </p>
            <RuleCardSkin
              :rule="guardrail"
              selectable
              :selected="bulkSelectedIds.has(guardrail.id)"
              @select="handleRuleSelect"
              @edit="editGuardrail"
              @delete="deleteGuardrail"
            />
          </div>
        </section>
      </div>
      <AddRuleField
        :placeholder="
          t('POLARIS.ASSISTANTS.GUARDRAILS.ADD.SUGGESTED.PLACEHOLDER')
        "
        :label="t('POLARIS.ASSISTANTS.GUARDRAILS.ADD.NEW.TITLE')"
        :categories="categoryOptions"
        @add="addGuardrail"
      />
    </template>
  </PageLayout>
</template>
